import React, { useState } from 'react'
import styled, { css } from 'styled-components'

import { Light as SyntaxHighlighter } from 'react-syntax-highlighter'
import bash from 'react-syntax-highlighter/dist/esm/languages/hljs/bash'
import csharp from 'react-syntax-highlighter/dist/esm/languages/hljs/csharp'
import cssStyling from 'react-syntax-highlighter/dist/esm/languages/hljs/css'
import go from 'react-syntax-highlighter/dist/esm/languages/hljs/go'
import java from 'react-syntax-highlighter/dist/esm/languages/hljs/java'
import javascript from 'react-syntax-highlighter/dist/esm/languages/hljs/javascript'
import kotlin from 'react-syntax-highlighter/dist/esm/languages/hljs/kotlin'
import php from 'react-syntax-highlighter/dist/esm/languages/hljs/php'
import python from 'react-syntax-highlighter/dist/esm/languages/hljs/python'
import ruby from 'react-syntax-highlighter/dist/esm/languages/hljs/ruby'
import swift from 'react-syntax-highlighter/dist/esm/languages/hljs/swift'
import nightOwl from 'react-syntax-highlighter/dist/esm/styles/hljs/night-owl'

import useActionAndReset from '../../hooks/useActionAndReset'

import CaretSVG from '../../../static/images/caret.inline.svg'

SyntaxHighlighter.registerLanguage('bash', bash)
SyntaxHighlighter.registerLanguage('csharp', csharp)
SyntaxHighlighter.registerLanguage('css', cssStyling)
SyntaxHighlighter.registerLanguage('go', go)
SyntaxHighlighter.registerLanguage('java', java)
SyntaxHighlighter.registerLanguage('javascript', javascript)
SyntaxHighlighter.registerLanguage('kotlin', kotlin)
SyntaxHighlighter.registerLanguage('php', php)
SyntaxHighlighter.registerLanguage('python', python)
SyntaxHighlighter.registerLanguage('ruby', ruby)
SyntaxHighlighter.registerLanguage('swift', swift)

const LINES_LIMIT = 10

const CodeBlock = styled.div`
  background-color: ${(p) => p.theme.chameleon.colors.pusher.codeblockHeaderBg};
  border-radius: 8px;
  font-size: 14px;
  margin: 16px 0;
  overflow: hidden;

  .linenumber {
    opacity: 0.5;
  }

  .codeStyle {
    background-color: ${(p) => p.theme.chameleon.colors.pusher.codeblockBg}!important;
  }
`

const Code = styled.code`
  background: ${(p) => p.theme.blueishGreyLight};
  color: ${(p) => p.theme.purpleDark};
  padding: 0 8px;
`
const Header = styled.button`
  display: flex;
  align-items: center;
  justify-content: space-between;
  background-color: ${(p) => p.theme.chameleon.colors.pusher.codeblockHeaderBg};
  color: #fff;
  font-family: 'Maison Neue';
  font-weight: 600;
  padding: 16px;
  text-transform: uppercase;
  width: 100%;
`
const Container = styled.div`
  background-color: ${(p) => p.theme.chameleon.colors.pusher.codeblockBg}!important;
  height: ${(p) => (p.$isCollapsed ? Math.min(p.numOfLines, LINES_LIMIT) * 24 + 12 : p.numOfLines * 24 + 12)}px;
  transition: height 200ms ease;
`
const Footer = styled.div`
  background-color: ${(p) => (p.$isCollapsed ? 'transparent' : p.theme.chameleon.colors.pusher.codeblockBg)};
  color: #fff;
  padding: 16px;
  position: relative;
  z-index: 0;
  transition: background-color ease 200ms;

  ::after {
    content: '';
    display: none;
    position: absolute;
    left: 0;
    right: 0;
    top: -50px;
    bottom: 0;
    background: rgb(0, 0, 0);
    background: linear-gradient(0deg, rgba(0, 0, 0, 1) 0%, rgba(0, 0, 0, 0) 100%);
    z-index: -1;
  }

  ${(p) =>
    p.$isCollapsed &&
    css`
      ::after {
        display: block;
      }
    `}
`
const CopyCodeButton = styled.button`
  background-color: ${(p) => (p.$state === 'initial' ? '#fff' : p.theme.successGreen)};
  color: ${(p) => (p.$state === 'initial' ? p.theme.chameleon.colors.pusher.codeblockHeaderBg : '#fff')};
  padding: 8px 16px;
  border-radius: 4px;
  font-weight: 600;
  font-family: 'Maison Neue';
  cursor: pointer;
`
const CaretIcon = styled(CaretSVG)`
  transform: ${(p) => (p.$isCollapsed ? 'rotate(0deg)' : 'rotate(180deg)')};
  transition: transform ease 300ms;
`

const CodeSnippet = ({ className, inline, children, ...props }) => {
  const position = props.node.position
  const numOfLines = position ? position.end.line - position.start.line - 1 : null
  const isExpandable = numOfLines > LINES_LIMIT

  const [isCollapsed, setIsCollapsed] = useState(isExpandable)
  const { state, triggerAction } = useActionAndReset({
    action: () => {
      navigator.clipboard.writeText(children[0])
    },
  })

  const match = /language-(\w+)/.exec(className || '')
  const codeLanguage = match ? match[1] : 'bash'

  const handleSnippetToggle = () => {
    if (!isExpandable) return
    setIsCollapsed((prev) => !prev)
  }

  if (inline || numOfLines === 1)
    return (
      <Code className={className} {...props}>
        {children}
      </Code>
    )

  return (
    <CodeBlock>
      <Header onClick={handleSnippetToggle}>
        <p>{codeLanguage}</p>
        {isExpandable && <CaretIcon $isCollapsed={isCollapsed} />}
      </Header>

      <Container numOfLines={numOfLines} $isCollapsed={isCollapsed}>
        <SyntaxHighlighter
          style={nightOwl}
          language={codeLanguage}
          className="codeStyle"
          showLineNumbers={true}
          {...props}
        >
          {String(children).trim()}
        </SyntaxHighlighter>
      </Container>

      <Footer $isCollapsed={isCollapsed}>
        <CopyCodeButton $state={state} onClick={triggerAction}>
          {state === 'initial' ? 'Copy' : 'Copied'}
        </CopyCodeButton>
      </Footer>
    </CodeBlock>
  )
}

export default CodeSnippet
